import { PageTitle } from "../../components";
import Menu from "../../components/VerticalMenu";
import { useEffect, useState } from "react";
import { Card, Col, Row, Statistic, Table, Tag, Spin } from "antd";
import {
  UserOutlined,
  TeamOutlined,
  TrophyOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";
import { competitionsApi, newsApi, dashboardApi } from "../../_helpers/apiService";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/lib/table";
import { useSelector } from "react-redux";

interface DashboardStats {
  totalArchers: number;
  clubArchers: number;
  totalTeams: number;
  totalReferees: number;
  totalLeaders: number;
  totalCompetitions: number;
  totalRanks: number;
}

function Home() {
  const userData = useSelector((state: any) => state.authentication.userData);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalArchers: 0,
    clubArchers: 0,
    totalTeams: 0,
    totalReferees: 0,
    totalLeaders: 0,
    totalCompetitions: 0,
    totalRanks: 0,
  });
  const [recentCompetitions, setRecentCompetitions] = useState<any[]>([]);
  const [latestNews, setLatestNews] = useState<any[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Check if user is admin
        const isAdmin = userData.role === "admin";

        if (isAdmin || userData.role === "superadmin") {
          // Fetch all data in parallel for admin/superadmin users
          const [
            statsResponse,
            allCompetitionsResponse,
            newsResponse,
          ] = await Promise.all([
            // Dashboard stats from backend (efficient SQL counts)
            dashboardApi.getStats(CURRENT_CLUB_ID),
            // All Competitions for recent list
            competitionsApi.getAll({ club_id: CURRENT_CLUB_ID, limit: 10, sort_by: 'start_date', sort_order: 'desc' }),
            // Latest News
            newsApi.getAll({ club_id: CURRENT_CLUB_ID, limit: 5, sort_by: 'created_at', sort_order: 'desc' }),
          ]);

          // Process latest news
          const newsData = newsResponse.data.items.map((news: any) => ({
            id: news.id,
            title: news.title,
            description: news.description,
            createdAt: news.created_at,
          }));
          setLatestNews(newsData);

          // Process recent competitions
          const recentComps = allCompetitionsResponse.data.items.map((comp: any) => ({
            id: comp.id,
            name: comp.name,
            startDate: comp.start_date,
            status: comp.status,
            location: comp.location,
          }));
          setRecentCompetitions(recentComps);

          // Set all stats from backend response
          setStats({
            totalArchers: statsResponse.data.total_archers,
            clubArchers: statsResponse.data.club_archers,
            totalTeams: statsResponse.data.total_teams,
            totalReferees: statsResponse.data.total_referees,
            totalLeaders: statsResponse.data.total_leaders,
            totalCompetitions: statsResponse.data.total_competitions,
            totalRanks: statsResponse.data.total_ranks,
          });
        } else {
          // For referee and leader roles, only fetch competition data
          const [
            activeCompetitionsResponse,
            completedCompetitionsResponse,
            allCompetitionsResponse,
          ] = await Promise.all([
            // Active Competitions
            competitionsApi.getAll({ club_id: CURRENT_CLUB_ID, status: 'open', limit: 1000 }),
            // Completed Competitions
            competitionsApi.getAll({ club_id: CURRENT_CLUB_ID, status: 'finished', limit: 1000 }),
            // All Competitions for recent list
            competitionsApi.getAll({ club_id: CURRENT_CLUB_ID, limit: 10, sort_by: 'start_date', sort_order: 'desc' }),
          ]);

          // Process recent competitions
          const recentComps = allCompetitionsResponse.data.items.map((comp: any) => ({
            id: comp.id,
            name: comp.name,
            startDate: comp.start_date,
            status: comp.status,
            location: comp.location,
          }));
          setRecentCompetitions(recentComps);

          // Set only competition stats
          setStats({
            totalArchers: 0,
            clubArchers: 0,
            totalTeams: 0,
            totalReferees: 0,
            totalLeaders: 0,
            totalCompetitions: activeCompetitionsResponse.data.total + completedCompetitionsResponse.data.total,
            totalRanks: 0,
          });
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userData.role]);

  const competitionColumns: ColumnsType<any> = [
    {
      title: "Тэмцээний нэр",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Байршил",
      dataIndex: "location",
      key: "location",
      width: 150,
    },
    {
      title: "Огноо",
      dataIndex: "startDate",
      key: "startDate",
      width: 120,
      render: (date) => {
        if (date) {
          return dayjs(date).format("YYYY-MM-DD");
        }
        return "-";
      },
    },
    {
      title: "Төлөв",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={status === "open" ? "green" : "blue"}>
          {status === "open" ? "Нээлттэй" : "Дууссан"}
        </Tag>
      ),
    },
  ];

  const newsColumns: ColumnsType<any> = [
    {
      title: "№",
      key: "index",
      width: 80,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Гарчиг",
      dataIndex: "title",
      key: "title",
      width: 200,
    },
    {
      title: "Тайлбар",
      dataIndex: "description",
      key: "description",
      width: 250,
    },
    {
      title: "Огноо",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => {
        if (date) {
          return dayjs(date).format("YYYY-MM-DD");
        }
        return "-";
      },
    },
  ];

  return (
    <>
      <PageTitle title="Тавтай морилно уу" description="Нүүр хуудас" />
      <div className="page-content-wrapper">
        <div className="container-fluid">
          {loading ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {(userData.role === "admin" || userData.role === "superadmin") ? (
                <>
                  <Row gutter={[16, 16]}>
                    {userData.role === "superadmin" && (
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Нийт харваачид"
                            value={stats.totalArchers}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: "#722ed1" }}
                          />
                        </Card>
                      </Col>
                    )}
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title={userData.role === "superadmin" ? "Холбооны харваачид" : "Нийт харваачид"}
                          value={stats.clubArchers}
                          prefix={<UserOutlined />}
                          valueStyle={{ color: "#3f8600" }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Нийт багууд"
                          value={stats.totalTeams}
                          prefix={<TeamOutlined />}
                          valueStyle={{ color: "#1890ff" }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Нийт тэмцээнүүд"
                          value={stats.totalCompetitions}
                          prefix={<TrophyOutlined />}
                          valueStyle={{ color: "#cf1322" }}
                        />
                      </Card>
                    </Col>
                    {userData.role !== "superadmin" && (
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Нийт цол"
                            value={stats.totalRanks}
                            prefix={<RiseOutlined />}
                          />
                        </Card>
                      </Col>
                    )}
                  </Row>

                  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    {userData.role === "superadmin" && (
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Нийт цол"
                            value={stats.totalRanks}
                            prefix={<RiseOutlined />}
                          />
                        </Card>
                      </Col>
                    )}
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Нийт шүүгчид"
                          value={stats.totalReferees}
                          prefix={<UserOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Багийн ахлагчид"
                          value={stats.totalLeaders}
                          prefix={<TeamOutlined />}
                        />
                      </Card>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24} lg={12}>
                      <Card title="Сүүлийн тэмцээнүүд" bordered={false}>
                        <Table
                          columns={competitionColumns}
                          dataSource={recentCompetitions}
                          pagination={false}
                          rowKey="id"
                          size="small"
                          scroll={{ x: 'max-content' }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Card title="Сүүлийн мэдээ" bordered={false}>
                        <Table
                          columns={newsColumns}
                          dataSource={latestNews}
                          pagination={false}
                          rowKey="id"
                          size="small"
                          scroll={{ x: 'max-content' }}
                        />
                      </Card>
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  {/* For referee and leader roles, only show competition stats */}
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={8}>
                      <Card>
                        <Statistic
                          title="Нийт тэмцээнүүд"
                          value={stats.totalCompetitions}
                          prefix={<TrophyOutlined />}
                          valueStyle={{ color: "#cf1322" }}
                        />
                      </Card>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24}>
                      <Card title="Сүүлийн тэмцээнүүд" bordered={false}>
                        <Table
                          columns={competitionColumns}
                          dataSource={recentCompetitions}
                          pagination={false}
                          rowKey="id"
                          size="small"
                          scroll={{ x: 'max-content' }}
                        />
                      </Card>
                    </Col>
                  </Row>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
export default Home;
